import { useState, useEffect, useRef, useCallback } from 'react';
import {
  DEFAULT_PANEL_WIDTH,
  MIN_PANEL_WIDTH,
  getMaxPanelWidth,
  getWidePanelWidth,
} from './aiChatLayout';

/**
 * Panel open width, drag-resize, and viewport clamping for the Vanti chat drawer.
 */
export function useAIChatPanelResize() {
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem('vanti.panelWidth') ?? '', 10);
    return saved && saved >= MIN_PANEL_WIDTH ? saved : DEFAULT_PANEL_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef<any>(null);
  const resizeStartWidth = useRef<any>(null);
  const panelWidthRef = useRef(panelWidth);

  const maxPanelWidth = getMaxPanelWidth();
  const widePanelWidth = getWidePanelWidth();
  const isNarrowPanel = panelWidth < 400;

  useEffect(() => {
    panelWidthRef.current = panelWidth;
  }, [panelWidth]);

  const onResizeMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      resizeStartX.current = e.clientX;
      resizeStartWidth.current = panelWidth;
      setIsResizing(true);
    },
    [panelWidth]
  );

  useEffect(() => {
    if (!isResizing) return;
    const onMouseMove = (e) => {
      const delta = resizeStartX.current - e.clientX;
      const next = Math.max(
        MIN_PANEL_WIDTH,
        Math.min(getMaxPanelWidth(), resizeStartWidth.current + delta)
      );
      setPanelWidth(next);
    };
    const onMouseUp = () => {
      setIsResizing(false);
      localStorage.setItem('vanti.panelWidth', String(panelWidthRef.current));
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isResizing]);

  useEffect(() => {
    const onResize = () => {
      const max = getMaxPanelWidth();
      setPanelWidth((w) => {
        if (w > max) {
          localStorage.setItem('vanti.panelWidth', String(max));
          return max;
        }
        return w;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return {
    panelWidth,
    setPanelWidth,
    isResizing,
    onResizeMouseDown,
    maxPanelWidth,
    widePanelWidth,
    isNarrowPanel,
  };
}
